import { defineComponent as _defineComponent } from 'vue'
import BendSummary from '@/components/common/BendSummary.vue';
import LayoutGrid from '@/views/dashboard/LayoutGrid.vue';
import NewFeatureBadge from '../NewFeatureBadge.vue';
import WidgetContainer from '@/components/Charts/widgetContainer/WidgetContainer.vue';
import { BendLayoutTab } from './BendLayoutTab';
import { FilterTimeSpanEnum } from '@/models/enums/FilterTimeSpanEnum';
import { FilterTimeAxisSpanEnum } from '@/models/enums/FilterTimeAxisSpanEnum';
import { computed, ref, watch } from 'vue';
import {
  availableDashboardLayouts,
  DashboardLayout,
} from '@/models/Charts/dashboardDefinition';
import WidgetDefinition from '@/models/Charts/widgetDefinition';
import { indexedWidgetDefinitions } from '@/utils/misc';
import { WidgetEnum } from '@/models/enums/WidgetEnum';
import { currentUser } from '@/utils/permissionUtils';
import { BetaFeature } from '@/models/enums/BetaFeature';
import { dateRangeToDateStrings } from '@/utils/dates';
import { useI18n } from '@/composables/i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'BendLayout',
  props: {
    activeTab: null,
    selectedDevices: null,
    dateRange: null,
    selectedTimespan: null,
    selectedShifts: null,
    selectedTimeAxisSpan: null
  },
  emits: ["change:tab"],
  setup(__props: any, { emit }: { emit: ((e: 'change:tab', tab: BendLayoutTab) => void), expose: any, slots: any, attrs: any }) {

const props = __props as {
  activeTab: BendLayoutTab;
  selectedDevices: string[];
  dateRange: [Date, Date];
  selectedTimespan: FilterTimeSpanEnum;
  selectedShifts: number[];
  selectedTimeAxisSpan: FilterTimeAxisSpanEnum;
};





const gridLayout = availableDashboardLayouts[DashboardLayout.TwoByThree];
const tableViewLayout = availableDashboardLayouts[DashboardLayout.OneByOne];

const activeTabInternal = ref(BendLayoutTab.BendOverview);
const widgetDefinitions = ref<WidgetDefinition[]>([]);
// HACK: This shouldn't be needed but, without it, when we navigate
// (not reload) to the Bend page, the widgets aren't rendered. It looks like
// the dashboard-card elements aren't recreated after updating
// widgetDefinition.
const areWidgetDefinitionsReady = ref(false);

const { t } = useI18n();

function onTabChanged(tab: BendLayoutTab) {
  emit('change:tab', tab);
  widgetDefinitions.value = createWidgetDefinitions();
}

function createWidgetDefinitions(): WidgetDefinition[] {
  const initialTimeFilter = isTrendsTabActive.value
    ? dateRangeString.value
    : props.selectedTimespan;

  return indexedWidgetDefinitions([
    new WidgetDefinition(
      isTrendsTabActive.value
        ? WidgetEnum.BendingAvailabilityHist
        : WidgetEnum.BendingAvailability,
      props.selectedDevices,
      initialTimeFilter,
      props.selectedShifts,
      undefined,
      props.selectedTimeAxisSpan,
    ),
    new WidgetDefinition(
      WidgetEnum.BendStatus,
      props.selectedDevices,
      undefined,
      [],
    ),
    new WidgetDefinition(
      isTrendsTabActive.value
        ? WidgetEnum.BendingTimeBetweenBendHist
        : WidgetEnum.BendingTimeBetweenBend,
      props.selectedDevices,
      initialTimeFilter,
      props.selectedShifts,
      undefined,
      props.selectedTimeAxisSpan,
    ),
    new WidgetDefinition(
      isTrendsTabActive.value
        ? WidgetEnum.BendingPerformancePartHist
        : WidgetEnum.BendingPerformancePart,
      props.selectedDevices,
      initialTimeFilter,
      props.selectedShifts,
      undefined,
      props.selectedTimeAxisSpan,
    ),
    new WidgetDefinition(
      WidgetEnum.BendingPerformance,
      props.selectedDevices,
      props.selectedTimespan,
      props.selectedShifts,
    ),
    new WidgetDefinition(
      isTrendsTabActive.value
        ? WidgetEnum.BendingPerformanceHist
        : WidgetEnum.BendingTopParts,
      props.selectedDevices,
      initialTimeFilter,
      props.selectedShifts,
      undefined,
      props.selectedTimeAxisSpan,
    ),
  ]);
}

function updateWidgetDefinition(
  index: number,
  widgetDefinition: WidgetDefinition,
) {
  widgetDefinitions.value.splice(index, 1, widgetDefinition);
}

function changeToTab(tab: BendLayoutTab) {
  activeTabInternal.value = tab;
  onTabChanged(tab);
}

const isTrendsTabActive = computed<boolean>(
  () => activeTabInternal.value === BendLayoutTab.BendTrends,
);

const dateRangeString = computed<[string, string]>(() =>
  dateRangeToDateStrings(props.dateRange),
);

watch(
  () => props.activeTab,
  () => {
    activeTabInternal.value = props.activeTab;
    widgetDefinitions.value = createWidgetDefinitions();
    areWidgetDefinitionsReady.value = true;
  },
  { immediate: true },
);

watch(
  () => [
    props.selectedDevices,
    dateRangeString.value,
    props.selectedTimespan,
    props.selectedShifts,
    props.selectedTimeAxisSpan,
  ],
  () => {
    widgetDefinitions.value = widgetDefinitions.value.map((widgetDefinition) =>
      widgetDefinition.getCopy({
        deviceId: props.selectedDevices,
        timeFilter:
          isTrendsTabActive.value &&
          widgetDefinition.widget !== WidgetEnum.BendingPerformance
            ? dateRangeString.value
            : props.selectedTimespan,
        shifts: props.selectedShifts,
        axisSpan: props.selectedTimeAxisSpan,
      }),
    );
  },
);

const intradayAvailabilityWidgetDefinition = computed<WidgetDefinition>(
  () =>
    new WidgetDefinition(
      WidgetEnum.BendIntradayAvailabilityTable,
      props.selectedDevices,
      undefined,
      props.selectedShifts,
    ),
);

const bendProductionHistoryWidgetDefinition = computed<WidgetDefinition>(
  () =>
    new WidgetDefinition(
      WidgetEnum.BendProductionHistory,
      props.selectedDevices,
      dateRangeString.value,
      props.selectedShifts,
    ),
);

const hasBendBetaFeature = computed<boolean>(() =>
  currentUser().hasBetaFeature(BetaFeature.WorkCenterConsoleBend),
);

watch(hasBendBetaFeature, (isBetaEnabled, wasBetaEnabled) => {
  if (wasBetaEnabled && !isBetaEnabled) {
    if (
      props.activeTab === BendLayoutTab.BendSummary ||
      props.activeTab === BendLayoutTab.BendIntradayAvailability ||
      props.activeTab === BendLayoutTab.BendProductionHistory
    ) {
      changeToTab(BendLayoutTab.BendOverview);
    }
  } else if (
    props.activeTab === BendLayoutTab.BendOverview ||
    props.activeTab === BendLayoutTab.BendTrends
  ) {
    changeToTab(BendLayoutTab.BendSummary);
  }
});

return { __sfc: true,props, emit, gridLayout, tableViewLayout, activeTabInternal, widgetDefinitions, areWidgetDefinitionsReady, t, onTabChanged, createWidgetDefinitions, updateWidgetDefinition, changeToTab, isTrendsTabActive, dateRangeString, intradayAvailabilityWidgetDefinition, bendProductionHistoryWidgetDefinition, hasBendBetaFeature, BendSummary, LayoutGrid, NewFeatureBadge, WidgetContainer, BendLayoutTab }
}

})